/**
 * BMW fleet integration search-registry section.
 *
 * Wired into `src/constants/search-registry/index.ts` via
 * `import { bmwSearchEntries }` plus a `...bmwSearchEntries` spread in the
 * exported registry object.
 *
 * Keys are the full router paths, matching the shape used by
 * `sections/individual-partners.ts`.
 */

import type { SearchRegistry } from '@/constants/search-registry/types';

export const bmwSearchEntries: SearchRegistry = {
  '/individual-partners/bmw': {
    keywords: [
      'bmw',
      'bmw fleet',
      'bmwfleet',
      'bmw overview',
      'bmw navigation',
      'bmw documents',
      'available documents',
      'where to find',
      'test system',
      'demo api',
      'test data',
      'developer center',
      'whitelist',
      'fuel cards',
      'permission groups',
      'stations',
      'vin',
      'driver',
      'fuel card',
      'fuel card process',
      'sync monitor',
      'change history',
      'api interaction log',
      'api reference',
      'import simulator',
    ],
    questions: [
      'Where do I find the right BMW page?',
      'What is in the BMW Partner Hub?',
      'Which role do I need to order BMW fuel cards?',
      'What kind of documents are available for BMW Fleet?',
      'Are the BMW documentation and demo APIs production systems?',
      'Which BMW page tells me whether the latest delivery ran?',
      'Where can I inspect raw BMW API requests and responses?',
      'Where do I find BMW vehicles and drivers?',
      'Where is the BMW fuel-card process explained?',
    ],
    faqs: [
      {
        q: 'Where should I start with the BMW fleet pages?',
        a: 'Open the Partner Hub overview. It has one folder per area: Integration (API Reference, API Guide, API Log, API Credentials), Fleet Setup (Permission Groups, Configuration), Vehicles & Drivers, Fuelling, Sites & Stations (Station Coverage, Transition Plan), Car Wash, Fuel Cards, Invoicing (Transactions, Reconciliation, Invoices) and Support. Pages marked Planned show what ryd will provide.',
      },
      {
        q: 'What documents and tools are available for BMW Fleet?',
        a: 'The Partner Hub provides one API reference (authentication, Whitelist API, fuel-card API), the API Guide, the whitelist records, sync monitor, change history, guides and simulator, the fuel-card process, the Tankgruppe matrix, the temporary station transition plan, the API log and API credentials. Start on the Partner Hub overview to choose by area.',
      },
      {
        q: 'Do the BMW documentation and demo APIs use production?',
        a: 'No. The BMW documentation examples, interactive demo APIs, simulator actions, and operational records currently use the test system and test data. Do not treat them as production fleet state.',
      },
      {
        q: 'Which BMW specifications are safe to treat as final?',
        a: 'BMW builds against one document, the BMW API Reference: authentication, the Whitelist API and the fuel-card API. Both APIs are available on the staging gateway first; production base URLs follow when they go live. The general Fleet API (v1.3.0) is not part of the BMW integration.',
      },
    ],
  },

  '/individual-partners/bmw/api-reference': {
    keywords: [
      'bmw',
      'bmw api reference',
      'partner api',
      'openapi',
      'swagger',
      'whitelist api',
      'fuel card api',
      'auth direct',
      'auth delegated',
      'introspect',
      'endpoints',
    ],
    questions: [
      'Where is the BMW API specification?',
      'How do I download the BMW OpenAPI spec?',
      'Which fields does the BMW fuel-card order call take?',
    ],
    faqs: [
      {
        q: 'Where is the BMW API specification?',
        a: 'The BMW API Reference page shows one OpenAPI document in Swagger UI with authentication (POST /auth/direct, /auth/delegated, /auth/introspect), the Whitelist API and the fuel-card API. ryd loads it each time the page opens; it is not stored in the portal. Use the OpenAPI button for a copy.',
      },
    ],
  },

  '/individual-partners/bmw/api-guide': {
    keywords: [
      'bmw',
      'api guide',
      'getting started',
      'client credentials',
      'scope',
      'fuel_cards:read',
      'fuel_cards:write',
      'invalid_scope',
      'idempotency key',
      'postman collection',
      'error code',
    ],
    questions: [
      'Which scopes does BMW need for the whitelist and fuel-card APIs?',
      'Where is the Postman collection for the BMW Whitelist API?',
      'Why does my token request fail with invalid_scope?',
      'How do I retry a BMW API call safely?',
    ],
    faqs: [
      {
        q: 'Which scopes does BMW need?',
        a: 'Request scope "whitelist fuel_cards:read fuel_cards:write" in one POST /auth/direct call. whitelist opens the Whitelist API. fuel_cards:read (GET) and fuel_cards:write (POST) open the fuel-card API and are restricted: ryd grants them per environment. The BMW API Guide explains them.',
      },
      {
        q: 'Why does POST /auth/direct answer invalid_scope?',
        a: 'The token request asked for a restricted scope, such as fuel_cards:write, that ryd has not granted to this client in this environment. Ask ryd to grant it; until then, request only the scopes you have.',
      },
    ],
  },

  '/individual-partners/bmw/fuel-cards/staging-tests': {
    keywords: [
      'bmw',
      'fuel card',
      'staging',
      'test cases',
      'simulator',
      'simulated bp',
      'test order',
      'rejected',
      'issued',
      'sim8526',
      'simskip',
    ],
    questions: [
      'How do I test a rejected BMW fuel-card order on staging?',
      'Which fuel-card cases can I test without BP?',
      'How do I choose the simulated BP answer for a test order?',
    ],
    faqs: [
      {
        q: 'How do I test a fuel-card case on staging?',
        a: 'Switch the Partner Hub to staging and open Fuel Cards → Staging Tests. Every case is listed; for an order case click Order this case: Order a Card opens with a TIM-ID and PIN that make the simulated BP give that answer. ryd Ops builds the file and polls, or the scheduled jobs do it.',
      },
      {
        q: 'Why is BP simulated on staging?',
        a: "BP has no test system for ryd yet. On ryd staging a simulator reads the request files and answers in BP's format and codes. Production always talks to BP.",
      },
    ],
  },
  '/individual-partners/bmw/fuel-card-process': {
    keywords: [
      'bmw',
      'fuel card process',
      'aral fuel card',
      'order status',
      'requested',
      'in production',
      'issued',
      'delivered',
      'cancellation requested',
      'cancelled',
      'rejected',
      'expired',
      'bp',
      's2s',
      'request file',
      'response file',
      'pin change',
      'block card',
      'stolen card',
      'daily poll',
    ],
    questions: [
      'What do the BMW fuel-card statuses mean?',
      'How long does it take until an ordered fuel card is issued?',
      'How does ryd exchange fuel-card orders with BP?',
      'When is a lost or stolen fuel card blocked?',
      'Can I cancel a fuel-card order that is in production?',
    ],
    faqs: [
      {
        q: 'How long until an ordered fuel card is ISSUED?',
        a: 'ryd sends waiting orders to BP in one file on business days at 10:00 (Europe/Berlin). BP processes the file in about 3.5 hours by its own estimate, and ryd reads the result within 15 minutes, so an order placed before 10:00 is usually ISSUED the same afternoon and otherwise the next business day. TIM sees it in its next daily poll.',
      },
      {
        q: 'When is a lost or stolen card blocked?',
        a: 'A cancellation on an issued card returns 202 CANCELLATION_REQUESTED: the block is queued for the next file, or sent at once for a stolen card where the program allows it. The card counts as blocked only when the order becomes CANCELLED.',
      },
      {
        q: 'Can I cancel an order that is IN_PRODUCTION?',
        a: 'No. The order is at BP; the API answers 409 ORDER_IN_PRODUCTION. Retry once it is ISSUED, which queues a block.',
      },
    ],
  },

  '/individual-partners/bmw/change-history': {
    keywords: [
      'bmw',
      'interactions',
      'interaction log',
      'audit',
      'audit trail',
      'change audit',
      'who changed this vin',
      'before after',
      'diff',
      'api call log',
      'request body',
      'response body',
      'http log',
      'correlation id',
      'import jobs',
      'job status',
      'needs confirmation',
      'guardrail',
      'removal ratio',
      'traceability',
      'actor',
      'job id',
    ],
    questions: [
      'Who removed this VIN from the whitelist, and when?',
      'Which import job caused this change?',
      'Can I see the request and response body of a BMW whitelist API call?',
      'Why did this sync apply nothing?',
    ],
    faqs: [
      {
        q: 'How do I find out who changed a whitelist entry?',
        a: 'Open BMW Change History and filter by entry key. Each row shows the action, actor, source job, and entry state before and after. Filtering by job ID reconstructs a whole sync.',
      },
      {
        q: 'Can I see the raw request and response bodies of BMW whitelist calls?',
        a: 'Open the separate BMW API Interaction Log. It records portal-made calls with bodies, status, and duration, but it cannot show BMW server-to-server traffic because TIM calling ryd overnight does not pass through the portal. Change History remains the authoritative record of what those server-side calls changed.',
      },
    ],
  },

  '/individual-partners/bmw/vins': {
    keywords: [
      'bmw',
      'vins',
      'vin list',
      'uploaded vins',
      'vin whitelist',
      'vehicle list',
      'vehicle status',
      'whitelist entries',
      'entry status',
      'effective status',
      'pending start',
      'expired',
      'validity window',
      'active from',
      'active until',
      'activeStartDate',
      'activeEndDate',
      'end date',
      'set end date',
      'override',
      'licence plate',
      'license plate',
      'tim id',
      'pool car',
      'drivetrain',
    ],
    questions: [
      'Which VINs are currently authorised?',
      'Why is this VIN not authorised at the pump?',
      'How do I stop a vehicle being authorised from a given date?',
      'What is the difference between status and effective state?',
    ],
    faqs: [
      {
        q: 'Why is a VIN with status ACTIVE still not authorised?',
        a: 'Because authorisation is status AND validity window. If the window has not started the effective state is PENDING_START; if it has ended the state is EXPIRED. The State column on the Uploaded VINs page shows that combined answer, which is what a pump or car wash acts on.',
      },
      {
        q: 'How do I end authorisation for one vehicle?',
        a: 'On the Uploaded VINs page, use End on the row and pick an end date with a reason and your name. It only ever shortens a window — extending authorisation has to come from TIM, which is the system of record. Whether the override survives the next FULL sync is still open with BMW.',
      },
    ],
  },

  '/individual-partners/bmw/drivers': {
    keywords: [
      'bmw',
      'drivers',
      'driver list',
      'uploaded drivers',
      'driver whitelist',
      'driver status',
      'whitelist entries',
      'effective status',
      'pending start',
      'expired',
      'validity window',
      'active from',
      'active until',
      'activeStartDate',
      'activeEndDate',
      'end date',
      'set end date',
      'leaver',
      'last working day',
      'override',
      'tim driver id',
      'cost centre',
      'cost center',
      'driver pii',
      'tisax',
    ],
    questions: [
      'Which drivers are currently authorised?',
      'How do I remove a leaver from the whitelist on their last working day?',
      'Why is this driver refused at the pump?',
    ],
    faqs: [
      {
        q: 'What is the right way to handle a driver who is leaving?',
        a: 'On the Uploaded Drivers page, use End on their row and set the end date to their last working day, with a reason and your name. That is better than waiting for TIM to send a delta, because it does not depend on a scheduled job firing on the right date.',
      },
    ],
  },

  '/individual-partners/bmw/sync-monitor': {
    keywords: [
      'bmw',
      'sync monitor',
      'delivery status',
      'latest delivery',
      'import job',
      'held back',
      'needs confirmation',
      'full sync',
      'delta sync',
      'test data',
    ],
    questions: [
      'Did the latest BMW vehicle or driver delivery run?',
      'Why was a BMW sync held back?',
      'How many records did the last BMW import change?',
    ],
    faqs: [
      {
        q: 'Where can I check whether the latest BMW delivery ran?',
        a: 'Open BMW Sync Monitor. It shows the latest vehicle and driver import jobs, their state, counts, timing, and whether a removal guardrail held a delivery back. The records currently come from the test system.',
      },
      {
        q: 'Why does a sync show NEEDS_CONFIRMATION?',
        a: 'The import crossed the configured removal guardrail, so it applied nothing and needs an attributable human review. Use Change History for entry-level attribution and the Import Simulator to reproduce the behavior with test data.',
      },
    ],
  },

  '/individual-partners/bmw/api-interaction-log': {
    keywords: [
      'bmw',
      'api interaction log',
      'api log',
      'gateway log',
      'critical api calls',
      'failed api calls',
      'whitelist api log',
      'fuel card api log',
      'raw request',
      'raw response',
      'http status',
      'duration',
      'correlation id',
      'demo api',
      'test system',
      'debug api',
    ],
    questions: [
      'Where can I inspect a failed BMW demo API call?',
      'Can I see the raw request and response for a BMW portal call?',
      'Does the BMW API log include overnight TIM traffic?',
      'Where do I see the whitelist and fuel-card calls TIM made to ryd?',
    ],
    faqs: [
      {
        q: 'What does the BMW API Log contain?',
        a: "The Gateway API Log lists the calls BMW systems made to the ryd gateway (whitelist and fuel-card APIs) from ryd's audit log: time, method, path, status, duration and correlation id, critical calls (every change and every 4xx/5xx) by default, for staging or production, up to 31 days per search. Request and response bodies open per call with secrets, PINs and tokens removed and card numbers masked; token requests and headers are never shown. Below it, the portal's own calls from this browser.",
      },
      {
        q: 'Does the API Log show every BMW integration call?',
        a: "Yes for calls through the ryd gateway with BMW's client credentials: the Gateway API Log reads the gateway's audit rows, including overnight TIM traffic. Token requests (/auth/*) are left out because they carry the client secret. Use Change History for the resulting whitelist changes.",
      },
    ],
  },
  '/individual-partners/bmw/permission-groups': {
    keywords: [
      'bmw',
      'permission groups',
      'tankgruppe',
      'tankgruppen',
      'permissionGroup',
      'matrix',
      'precedence',
      'aral karte',
      'high performance',
      'betriebsmittel',
    ],
    questions: [
      'What does BMW Tankgruppe 009 allow?',
      'Which Tankgruppe applies when a driver uses a pool vehicle?',
      'Which BMW permission groups may hold an ARAL card?',
    ],
    faqs: [
      {
        q: 'Which Tankgruppe applies when both a driver and a vehicle are known?',
        a: 'The driver group supersedes the vehicle group, in the mobile and the in-car app; the groups are never merged. The Permission Groups page shows the full matrix.',
      },
      {
        q: 'Which groups may hold an ARAL card?',
        a: 'Only 009, 010 and 011 according to BMW final matrix of 15.10.2026.',
      },
    ],
  },

  '/individual-partners/bmw/station-transition-plan': {
    keywords: [
      'bmw',
      'standortplan',
      'station transition',
      'plant-near',
      'werksnah',
      'tim terminal',
      'pos switch',
      'kassenumstellung',
      'leipzig',
      'münchen',
      'regensburg',
    ],
    questions: [
      'When does the TIM terminal end at a BMW plant station?',
      'From when can BMW drivers use the apps at Leipzig Autohof A14?',
      'Where can BMW vehicles get AdBlue near the plant?',
    ],
    faqs: [
      {
        q: 'When does TIM alt end at a station?',
        a: 'When the Circle K station switches to the new POS, because TIM alt works only with the old POS systems. It is removed only once TIM neu runs or the drivers use the apps. The Station Transition Plan lists the date per station (planning status 13.10.2026, dates can move).',
      },
    ],
  },
  '/individual-partners/bmw/fuel-cards': {
    keywords: [
      'bmw',
      'fuel card orders',
      'aral card',
      'card status',
      'purchased cards',
      'my cards',
      'pin change',
      'block card',
      'withdraw order',
      'card number',
    ],
    questions: [
      'Where do I see the BMW fuel cards and their status?',
      'How do I change the PIN of a BMW fuel card in the portal?',
      'How do I block a lost BMW fuel card?',
    ],
    faqs: [
      {
        q: 'Can I manage BMW fuel cards in the portal instead of through TIM?',
        a: 'Yes. Card Orders lists every card ordered through TIM or the portal with its status; open an order to change the PIN, block the card or withdraw an order not yet sent to ARAL. Order a Card places a new order. The same rules as the fuel-card API apply. Ordering and changing cards needs the Fuel Card Manager role; with the developer role the orders are read-only.',
      },
      {
        q: 'Why is the card number masked?',
        a: 'Card numbers are shown as ****1234. Show full number reveals it for 30 seconds after you give a reason; the request is logged with your name.',
      },
    ],
  },

  '/individual-partners/bmw/fuel-cards/new': {
    keywords: [
      'bmw',
      'order fuel card',
      'purchase fuel card',
      'buy aral card',
      'new card',
      'wish pin',
    ],
    questions: [
      'How do I order a BMW fuel card in the portal?',
      'Which PIN rules apply to a BMW fuel card?',
    ],
    faqs: [
      {
        q: 'How do I order a BMW fuel card in the portal?',
        a: 'Open Partner Hub > Fuel Cards > Order a Card, fill in the TIM-ID, card profile, delivery address and wish PIN, and confirm. The order goes to ARAL with the next daily file on business days at 10:00.',
      },
    ],
  },
  '/individual-partners/bmw/whitelist-batch-guide': {
    keywords: [
      'bmw',
      'whitelist batch',
      'batch api',
      'batches',
      'batch status',
      'upsert',
      'delete',
      'paused',
      'idempotency key',
      'sync',
      'best practices',
      'rate limit',
    ],
    questions: [
      'How do I keep the BMW whitelist in sync with TIM?',
      'How many entries can a BMW whitelist batch contain?',
      'What does COMPLETED_WITH_ERRORS mean for a whitelist batch?',
      'How do I retry a whitelist batch safely?',
    ],
    faqs: [
      {
        q: 'How do I keep the BMW whitelist in sync with TIM?',
        a: 'Page the current state (status ACTIVE and PAUSED), diff it against TIM, and send only the differences as batches of at most 150 UPSERT or DELETE entries, each with its own Idempotency-Key. Poll the batch until it is final and resend only failed entries. The Whitelist Batch API Guide explains each step.',
      },
      {
        q: 'Is there a full-sync or file upload for the whitelist?',
        a: 'No. BMW sends the whitelist through the API; an entry missing from a batch is left untouched, so deletions must be sent explicitly. The earlier file and import-session approaches are no longer used.',
      },
    ],
  },
  '/individual-partners/bmw/fleet-configuration': {
    keywords: ['bmw', 'fleet configuration', 'partner policy', 'tankgruppen', 'channels', 'limits'],
    questions: ['How is the BMW fleet configured at ryd?'],
    faqs: [
      {
        q: 'How is the BMW fleet configured at ryd?',
        a: 'BMW is one fleet at ryd; the twelve Tankgruppen become rule sets for stations, products, payment channels and limits. The Fleet Configuration page (planned) will show the set-up; the matrix is on Permission Groups.',
      },
    ],
  },

  '/individual-partners/bmw/driver-onboarding': {
    keywords: ['bmw', 'driver onboarding', 'registration link', 'deep link', 'driver status'],
    questions: ['How does a BMW driver get an app account?'],
    faqs: [
      {
        q: 'How does a BMW driver get an app account?',
        a: 'TIM registers the driver on the whitelist, ryd issues a personal registration link that BMW distributes, and the driver sets e-mail, password and second factor. The Driver Onboarding page (planned) will show the status of each driver.',
      },
    ],
  },

  '/individual-partners/bmw/payment-channels': {
    keywords: [
      'bmw',
      'payment channels',
      'mobile app',
      'in-car app',
      'aral card',
      'tim terminal',
      'tap to pay',
    ],
    questions: ['Which ways to pay do BMW drivers have?'],
    faqs: [
      {
        q: 'Which ways to pay do BMW drivers have?',
        a: 'Five: mobile app, in-car app, ARAL plastic card, the TIM terminal during the transition, and tap-to-pay abroad in phase 2. The Payment Channels page (planned) explains each.',
      },
    ],
  },

  '/individual-partners/bmw/station-coverage': {
    keywords: ['bmw', 'station coverage', 'lot 1', 'lot 2', 'stations', 'countries'],
    questions: ['Where can BMW drivers fuel through ryd?'],
    faqs: [
      {
        q: 'Where can BMW drivers fuel through ryd?',
        a: 'At the plant-near sites (Lot 2), about 240 Circle K / TotalEnergies sites nationwide (Lot 1), ARAL stations, and app-enabled stations in 13 countries abroad. Station Coverage (planned) will show the map.',
      },
    ],
  },

  '/individual-partners/bmw/car-wash': {
    keywords: ['bmw', 'car wash', 'plate recognition', 'anpr', 'qr code', 'wash limit'],
    questions: ['How do BMW vehicles get washed through ryd?'],
    faqs: [
      {
        q: 'How do BMW vehicles get washed through ryd?',
        a: 'By licence-plate recognition at tunnels (from 01.01.2027, site by site) or with a QR code from the app at rollover washes; at most two washes per plate per day. The Car Wash page is planned.',
      },
    ],
  },

  '/individual-partners/bmw/car-wash/circle-k-pos-reporting': {
    keywords: [
      'bmw',
      'circle k',
      'pcs',
      'prizma',
      'dover pos',
      'insertsale',
      'smartnow',
      'christ',
      'car wash sales',
      'api log',
    ],
    questions: [
      'Was a car wash sale registered in the Circle K POS?',
      'What did ryd send to Circle K for an order?',
    ],
    faqs: [
      {
        q: 'Where do I see what was sent to Circle K for an order?',
        a: 'Open Circle K POS Reporting, search the PCS Activity Log by the order UUID and open Request and response.',
      },
    ],
  },

  '/individual-partners/bmw/car-wash/circle-k-pcs-how-it-works': {
    keywords: ['bmw', 'circle k', 'pcs', 'how it works', 'insertsale', 'sale status', 'dover pos'],
    questions: [
      'How does a BMW wash sale reach the Circle K POS?',
      'What does a status in the Circle K PCS Activity Log mean?',
    ],
    faqs: [
      {
        q: 'What does EXCEPTION mean for a Circle K sale?',
        a: 'The sale stopped: unknown article, wrong data or 48 hours of retries. ryd fixes the station and sends it again. See How Circle K POS Reporting Works.',
      },
    ],
  },

  '/individual-partners/bmw/transactions': {
    keywords: ['bmw', 'transactions', 'transaction export', 'cost centre', 'lot flag'],
    questions: ['Where will BMW see all transactions?'],
    faqs: [
      {
        q: 'Where will BMW see all transactions?',
        a: 'On the Transactions page (planned): fuel, wash and operating supplies from all channels with TIM-ID, VIN, plate, card, cost centre and Lot flag, plus a daily export TIM can pull.',
      },
    ],
  },

  '/individual-partners/bmw/reconciliation': {
    keywords: ['bmw', 'reconciliation', 'settlement', 'matching', 'exceptions'],
    questions: ['How are BMW transactions reconciled before invoicing?'],
    faqs: [
      {
        q: 'How are BMW transactions reconciled before invoicing?',
        a: 'Each transaction is matched against the station or wash partner settlement; only matched and released transactions are invoiced, everything else becomes an exception with an owner. The Reconciliation page (planned) shows the states and the process.',
      },
    ],
  },

  '/individual-partners/bmw/invoices': {
    keywords: ['bmw', 'invoices', 'invoice status', 'zugferd', 'en 16931', 'correction invoice'],
    questions: ['Which statuses does a BMW invoice have?', 'How does BMW receive its invoices?'],
    faqs: [
      {
        q: 'Which statuses does a BMW invoice have?',
        a: 'Proposed: in preparation, ready, sent, accepted or rejected by BMW accounts payable, corrected, and paid. Invoices are electronic (ZUGFeRD / EN 16931) with a transaction annex. See the Invoices page (planned).',
      },
    ],
  },

  '/individual-partners/bmw/anomaly-reports': {
    keywords: ['bmw', 'anomaly reports', 'ryd verify', 'fraud', 'audit'],
    questions: ['How does ryd report anomalies to BMW?'],
    faqs: [
      {
        q: 'How does ryd report anomalies to BMW?',
        a: 'ryd verify checks transactions against rules (for example limits per vehicle per day, one session per vehicle, wash limit) and reports findings to BMW audit; standard reporting is planned for March 2027.',
      },
    ],
  },

  '/individual-partners/bmw/support': {
    keywords: ['bmw', 'support', 'hotline', 'card blocking', 'drive-off'],
    questions: ['How fast is a lost BMW fuel card blocked?'],
    faqs: [
      {
        q: 'How fast is a lost BMW fuel card blocked?',
        a: 'The support process aims to block a lost or stolen card within 4 hours; the card counts as blocked when its order is Cancelled. See Support Process (planned) and Card Orders.',
      },
    ],
  },
};
